import ApiService from '@/services/ApiService'
import AuthUserStateService from '@/services/AuthUserStateService'
import { User, Settings } from '@/store'
import { Network } from '@capacitor/network'
import SubService from '@/services/SubService'
import AppService from '@/services/AppService'
import StorageService from '@/services/StorageService.js'

class AuthService {
  /**
   * Provide nickname for new user. Otherwise, authenticate with facebookId and email.
   *
   * @param facebookId
   * @param email
   * @param nickname
   * @returns {Promise<*>}
   */
  async authenticate ({ facebookId, email, nickname }) {
    // console.log('AuthService: authenticating...', { facebookId, email, nickname })
    const { data: res } = await ApiService.createOrGetUser({ facebookId, email, nickname })

    if (!res.user) {
      console.error('Unable to authenticate or create a user', { facebookId, email, nickname })
      throw new Error('Unable to authenticate user')
    }

    // Store authenticated user data
    await User.setUser(res.user)

    // Sync user progress
    await AuthUserStateService.sync(User.getUser())

    await this.refresh3iToken(User.getUser())

    await SubService.logIn({
      id: res.user.id,
      email: res.user.email,
      displayName: res.user.nickname
    })

    return res.user
  }

  /**
   *
   * @param {Object} data
   * @param {string} data.email
   * @param {string} data.givenName
   * @param {string} data.familyName
   * @param {string} data.authorizationCode
   * @param {string} data.identityToken
   * @param {string} data.user
   * @returns {Promise<void>}
   */
  async authenticateWithApple (data) {
    const { data: res } = await ApiService.createOrGetUserWithApple(data)

    if (!res.user) {
      console.error('Unable to authenticate or create a user', {
        user: data.user,
        email: data.email
      })

      throw new Error('Unable to authenticate user')
    }

    // Store authenticated user data
    await User.setUser(res.user)

    // Sync user progress
    await AuthUserStateService.sync(res.user)

    const user = User.getUser()

    // Get 3i token
    await this.refresh3iToken(user)

    SubService.logIn({
      id: user?.id,
      email: user?.email,
      displayName: user?.nickname
    })

    return res.user
  }

  async refreshUser () {
    const status = await Network.getStatus()
    const u = User.getUser()

    if (status.connected && u?.id) {
      const { data } = await ApiService.getUser(u.id)
      await User.setUser(data.user)
    }
  }

  async logout () {
    await User.reset()
    await Promise.all([
      SubService.logOut(),
      AuthUserStateService.reset(),
      AppService.resetAppId(),
      this.refresh3iToken()
    ])
  }

  isAuth () {
    const user = User.getUser()
    return Boolean(user?.id)
  }

  getAuthUser() {
    return User.getUser()
  }

  async refresh3iToken(user) {
    await StorageService.set('3i_token', null)
    const { data } = await ApiService.get3iToken(user?.id)
    await StorageService.set('3i_token', data.token)
    return data.token
  }
}

export default new AuthService()
